import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Logo from 'media/images/logos/logo.png';
import { eventTagSources } from 'js/constants';
import regionTypes from 'js/enums/region-types.enum';
import ruleTypes from 'js/enums/rule-types.enum';
import {
    AbortError,
    RuleSet,
    api,
    copyContext,
    deepCopy,
    setDocumentTitle,
    exportToXLSX,
} from 'js/utils';
import Crumb from 'js/components/breadcrumbs/crumb';
import Layout from 'js/components/layout/layout';
import LoadingLayer from 'js/components/loading-layer/loading-layer';
import Navigation from 'js/components/navigation/navigation';
import SummaryFooter from 'js/components/summary-footer/summary-footer';
import { showToast } from 'js/components/toast/toast';
import TopBar from 'js/components/top-bar/top-bar';
import languageTypes from 'js/enums/language-types.enum';
import SaveButton from '../components/save-button';
import ReportDisplayDetail from './report-display-detail';

const contextChangesTemplate = {
    rules: [],
};

const ruleSortFn = (a, b) => {
    const ruleOrders = [ruleTypes.INCLUDED, ruleTypes.EXCLUDED];
    if (ruleOrders.indexOf(a.aggregation) < ruleOrders.indexOf(b.aggregation)) {
        return -1;
    }
    if (a.key < b.key) {
        return -1;
    }
    return 1;
};

function useContextChanges(initialContext, context) {
    const [contextChanges, setContextChanges] = useState(
        contextChangesTemplate,
    );

    useEffect(() => {
        const { rules } = context;
        const { rules: initialRules } = initialContext;
        if (!rules || !initialRules) {
            return;
        }

        const diffRules = [];

        initialRules.forEach((initialRule) => {
            const updatedRule = rules.find(
                (rule) => rule.key === initialRule.key,
            );

            if (updatedRule) {
                const addedKeywords = updatedRule.keywords.filter(
                    (x) => !initialRule.keywords.includes(x),
                );
                const removedKeywords = initialRule.keywords.filter(
                    (x) => !updatedRule.keywords.includes(x),
                );

                const addedTopics = updatedRule.topics.filter(
                    (x) => !initialRule.topics.includes(x),
                );
                const removedTopics = initialRule.topics.filter(
                    (x) => !updatedRule.topics.includes(x),
                );

                if (
                    addedKeywords.length ||
                    removedKeywords.length ||
                    addedTopics.length ||
                    removedTopics.length
                ) {
                    diffRules.push({
                        ...updatedRule,
                        addedKeywords,
                        removedKeywords,
                        addedTopics,
                        removedTopics,
                        isNew: false,
                    });
                }
            } else {
                diffRules.push({
                    ...initialRule,
                    addedKeywords: [],
                    removedKeywords: initialRule.keywords,
                    addedTopics: [],
                    removedTopics: initialRule.topics,
                    isNew: false,
                });
            }
        });

        rules.forEach((rule) => {
            const isAddedRule = !initialRules.some(
                (initialRule) => rule.key === initialRule.key,
            );

            if (isAddedRule) {
                diffRules.push({
                    ...rule,
                    addedKeywords: rule.keywords,
                    removedKeywords: [],
                    addedTopics: rule.topics,
                    removedTopics: [],
                    isNew: true,
                });
            }
        });

        setContextChanges({
            rules: diffRules.sort(ruleSortFn),
        });
    }, [initialContext, context]);

    return contextChanges;
}

export default function ReportInsightsDetailPage() {
    const { templateId, reportId } = useParams();
    const history = useHistory();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [scale, setScale] = useState(null);
    const [scaleLoading, setScaleLoading] = useState(true);
    const [scaleErrors, setScaleErrors] = useState({});
    const [priceLoading, setPriceLoading] = useState(false);
    const [hasDeletingError, setHasDeletingError] = useState(false);
    const [hasLoadingError, setHasLoadingError] = useState(false);
    const [hasSnapshotError, setHasSnapshotError] = useState(false);
    const [reports, setReports] = useState([]);
    const [report, setReport] = useState(null);
    const [template, setTemplate] = useState({});
    const [initialContext, setInitialContext] = useState({});
    const [context, setContext] = useState({});
    const [topicGroups, setTopicGroups] = useState([]);
    const [matchedPages, setMatchedPages] = useState([]);
    const [isMatchedPagesLoading, setMatchedPagesLoading] = useState(false);
    const [hasMatchedPagesError, setMatchedPagesError] = useState(false);

    const contextChanges = useContextChanges(initialContext, context);
    const { rules: ruleChanges } = contextChanges;
    const topicChangesCount = ruleChanges.reduce(
        (count, rule) =>
            count + rule.addedTopics.length + rule.removedTopics.length,
        0,
    );
    const keywordChangesCount = ruleChanges.reduce(
        (count, rule) =>
            count + rule.addedKeywords.length + rule.removedKeywords.length,
        0,
    );
    const hasUnsavedChanges = topicChangesCount + keywordChangesCount > 0;

    const getScaleScore = async (c) => {
        const { rules = [] } = c;
        const nonEmptyRules = rules.filter((rule) => rule.keywords.length);

        if (!nonEmptyRules.length) {
            setScaleErrors({});
            setScale(null);
            setScaleLoading(false);
            return;
        }

        setScaleLoading(true);
        setScaleErrors({});

        try {
            const { score } = await api()
                .contexts.action('scale')
                .create({
                    ...c,
                    rules: nonEmptyRules,
                    name: undefined,
                    group: undefined,
                });

            if (score === undefined || score < 0) {
                const errors = { score: 'Failed to calculate scale.' };

                throw errors;
            }
            setScaleLoading(false);
            setScale(score);
        } catch (err) {
            if (err instanceof AbortError) return;
            setScaleLoading(false);
            setScale(null);
            setScaleErrors(err);
        }
    };

    const getContextPrice = async (c) => {
        const { rules = [] } = c;
        const nonEmptyRules = rules.filter(
            (rule) =>
                rule.keywords.length || rule.topics.length || rule.logos.length,
        );

        if (!nonEmptyRules.length) {
            setPriceLoading(false);
            return;
        }

        setPriceLoading(true);

        try {
            const resp = await api()
                .contexts.action('price')
                .create({
                    ...c,
                    rules: nonEmptyRules,
                    name: undefined,
                    group: undefined,
                });
            setPriceLoading(false);
            setContext((prev) => ({ ...prev, ...resp }));
        } catch (err) {
            if (err instanceof AbortError) return;
            setPriceLoading(false);
        }
    };

    const getBreadcrumbs = () => {
        const breadcrumbs = [
            <Crumb key="home" to="/" label="4D" />,
            <Crumb key="pulse" to="/pulse/" label="Pulse" isCollapsible />,
        ];
        if (hasLoadingError) {
            breadcrumbs.push(<Crumb key="error" hasError />);
        } else if (loading) {
            breadcrumbs.push(<Crumb key="loading" isLoading />);
        } else {
            breadcrumbs.push(
                <Crumb
                    key="advertiser"
                    label={template.advertiser_name}
                    to={`/pulse/${template.advertiser}/`}
                    isCollapsible
                />,
                <Crumb key="report" label={template.name} />,
            );
        }

        return breadcrumbs;
    };

    const getMatchedPages = async (inspectedContext) => {
        setMatchedPages([]);
        setMatchedPagesLoading(true);
        setMatchedPagesError(false);

        try {
            const { results: list } = await api().matchedPagesAnalytics.create(
                inspectedContext,
                {
                    limit: 40,
                    region: regionTypes.ANY,
                },
            );

            setMatchedPages(
                list.map((item) => ({
                    brand_safety_contexts: [],
                    ...item,
                })),
            );
            setMatchedPagesLoading(false);
            setMatchedPagesError(false);
        } catch (err) {
            if (err instanceof AbortError) return;

            setMatchedPages([]);
            setMatchedPagesLoading(false);
            setMatchedPagesError(true);
        }
    };

    const switchReport = async (id) => {
        setLoading(true);
        setHasLoadingError(false);

        try {
            const reportData = await api().optimizeReports.retrieve(id, {
                verbose: true,
            });

            setReport(reportData);
        } catch (errors) {
            setHasLoadingError(true);
        } finally {
            setLoading(false);
        }
    };

    const loadData = async () => {
        setLoading(true);
        setHasLoadingError(false);

        try {
            const [
                { results: topicsData },
                { results: reportsData },
                templateData,
            ] = await Promise.all([
                api().topics.list({
                    ordering: 'group__name,name',
                    language: languageTypes.EN,
                    verbose: true,
                }),
                api().optimizeReports.list({
                    template: templateId,
                    ordering: '-end_date',
                }),
                api().optimizeReportTemplates.retrieve(templateId, {
                    verbose: true,
                }),
            ]);

            const { context: contextId } = templateData;
            const [contextData, reportData] = await Promise.all([
                api().contexts.retrieve(contextId, { verbose: true }),
                reportsData.length > 0
                    ? api().optimizeReports.retrieve(
                          reportId || reportsData[0].id,
                          { verbose: true },
                      )
                    : null,
            ]);
            const { rules } = contextData;
            const ruleSet = new RuleSet(rules);
            contextData.rules = ruleSet.rules;

            setReports(reportsData);
            setTemplate(templateData);
            setInitialContext(contextData);
            setContext(contextData);
            setReport(reportData);
            setTopicGroups(
                topicsData
                    .filter(
                        (topic, i) =>
                            !i || topic.group !== topicsData[i - 1].group,
                    )
                    .map((item) => ({
                        id: item.group,
                        name: item.group_name,
                        topics: topicsData.filter(
                            (topic) => topic.group === item.group,
                        ),
                    })),
            );
            getScaleScore(contextData);
            getContextPrice(contextData);
        } catch (errors) {
            setHasLoadingError(true);
        } finally {
            setLoading(false);
        }
    };

    const createSnapshot = async (dateRange) => {
        setHasSnapshotError(false);
        try {
            const snapshotTemplate = await api()
                .optimizeReportTemplates.action('refresh')
                .create({ id: templateId, ...dateRange });

            setTemplate(snapshotTemplate);
        } catch (errors) {
            setHasSnapshotError(true);
            throw errors;
        }
    };

    const deleteTemplate = async () => {
        setHasDeletingError(false);
        try {
            await api().optimizeReportTemplates.destroy(template.id);
            await history.push(`/pulse/${template.advertiser}/`);
        } catch (errors) {
            setHasDeletingError(true);
            throw errors;
        }
    };

    const toggleFavorite = async () => {
        const isFavorite = !template.is_favourite;
        try {
            await api().optimizeReportTemplates.favorite(
                template.id,
                isFavorite,
            );
            setTemplate({ ...template, is_favourite: isFavorite });
        } catch (err) {
            showToast(
                'There Was An Unexpected Error',
                <p>
                    It was not possible to{' '}
                    {isFavorite ? 'favorite' : 'unfavorite'} {template.name}.
                    Please try again in a few moments.
                </p>,
                10,
                'danger',
            );
        }
    };

    const changeContextRules = (rules) => {
        const updatedContext = {
            ...deepCopy(context),
            rules,
        };
        setContext(updatedContext);
        getScaleScore(updatedContext);
        getContextPrice(updatedContext);
    };

    const createContext = () => {
        copyContext(context, { namePrefix: '' });
        setContext(deepCopy(initialContext)); // avoids triggering the unsaved changes modal
    };

    const saveContext = async () => {
        setSaving(true);

        const nonEmptyRules = context.rules.filter(
            (rule) =>
                rule.keywords.length || rule.topics.length || rule.logos.length,
        );

        const method = api().contexts.update;
        const params = [context.id];

        params.push({
            ...context,
            rules: nonEmptyRules,
        });

        try {
            await method(...params, { verbose: true });
            setContext(deepCopy(initialContext));
            history.push(`/pulse/${context.advertiser}/`);
        } catch (err) {
            setSaving(false);
            showToast(
                'There Was An Unexpected Error',
                <p>
                    The context has not been saved. Please try again in a few
                    moments.
                </p>,
                null,
                'danger',
            );
        }
    };

    useLayoutEffect(() => {
        loadData();
        return () => api().abortAll();
    }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (template.name) {
            setDocumentTitle([template.name, 'Pulse']);
        } else {
            setDocumentTitle(['Pulse']);
        }
    }, [template.name]);

    const createExcelExport = async () => {
        const campaigns = [];
        const lineItems = [];
        template.campaigns.forEach((campaign) => {
            let name = campaign.campaign_name;
            let id = campaign.campaign_id;
            campaigns.push(name ? `${name} (${id})` : id);
            campaign.line_items.forEach((lineItem) => {
                name = lineItem.line_item_name;
                id = lineItem.line_item_id;
                lineItems.push(name ? `${name} (${id})` : id);
            });
        });

        const { campaignLabel = 'Campaign', lineItemLabel = 'Line Item' } =
            eventTagSources.find(({ id }) => id === template.source) || {};

        const ctr = report.impressions ? report.clicks / report.impressions : 0;
        const viewRate = report.impressions
            ? report.in_views / report.impressions
            : 0;

        const reportSummary = [
            [],
            ['Start Date', new Date(report.start_date)],
            ['End Date', new Date(report.end_date)],
            ['Client', context.advertiser_name],
            ['Context ID', context.id],
            ['Context Group Name', context.group_name],
            ['Context Name', context.name],
            ['Avocado ID', report.account],
            [`${campaignLabel} ID(s)`, campaigns.join(', ')],
            [
                `${lineItemLabel} ID(s)`,
                lineItems.join(', ') ||
                    `All ${campaignLabel} ${lineItemLabel}s`,
            ],
            [],
            ['Impressions', report.impressions],
            ['Clicks', report.clicks],
            ['CTR %', `${ctr * 100}%`],
            ['Viewable Impr.', report.in_views],
            ['View Rate %', `${viewRate * 100}%`],
        ];

        const dailyBreakdown = report.overviews.map((data) => ({
            Date: new Date(data.activity_date),
            Impressions: data.impressions,
            'Viewable Impr.': data.in_views,
            Clicks: data.clicks,
        }));

        const groupBreakdown = report.topic_groups.map((data) => ({
            Group: data.topic_group_name,
            Impressions: data.impressions,
            'Viewable Impr.': data.in_views,
            Clicks: data.clicks,
        }));

        const topicBreakdown = report.topics.map((data) => ({
            Group: topicGroups.find((group) =>
                group.topics.some(({ id }) => id === data.topic),
            ).name,
            Topic: data.topic_name,
            Impressions: data.impressions,
            'Viewable Impr.': data.in_views,
            Clicks: data.clicks,
        }));

        const keywordBreakdown = report.keywords
            ?.sort((a, b) => a.impressions - b.impressions)
            .slice(0, 100)
            .map((data) => ({
                Name: data.name,
                Impressions: data.impressions,
                'Viewable Impr.': data.in_views,
                Clicks: data.clicks,
            }));

        const sheets = [
            {
                name: 'Report Summary',
                data: reportSummary,
                title: template.name,
                logo: Logo,
            },
            { name: 'Daily Breakdown', data: dailyBreakdown },
            { name: 'Group Breakdown', data: groupBreakdown },
            { name: 'Topic Breakdown', data: topicBreakdown },
            { name: 'Keyword Breakdown', data: keywordBreakdown },
        ].filter(({ data }) => data.length);

        await exportToXLSX(sheets, `4D report - ${template.name}.xlsx`);
    };

    return (
        <Layout
            sidebar={<Navigation />}
            header={<TopBar breadcrumbs={getBreadcrumbs()} />}
            footer={
                !loading &&
                !hasLoadingError &&
                reports.length > 0 && (
                    <SummaryFooter
                        context={context}
                        contextChanges={contextChanges}
                        topics={report.topics}
                        priceChanging={priceLoading}
                        scaleScore={scale}
                        scaleChanging={scaleLoading}
                        scaleErrors={scaleErrors}
                        scaleReload={() => getScaleScore(context)}
                        onChangeContextRules={changeContextRules}
                        isShowChangeDetails
                        actionItems={
                            <SaveButton
                                hasUnsavedChanges={hasUnsavedChanges}
                                onCreateContext={createContext}
                                onSaveContext={saveContext}
                            />
                        }
                    />
                )
            }
            isFooterSticky
        >
            {saving && <LoadingLayer message="Saving Context" />}

            <ReportDisplayDetail
                report={report}
                reports={reports}
                template={template}
                context={context}
                loading={loading}
                hasLoadingError={hasLoadingError}
                hasDeletingError={hasDeletingError}
                hasSnapshotError={hasSnapshotError}
                hasUnsavedChanges={hasUnsavedChanges}
                onDelete={deleteTemplate}
                onDiscardChanges={() => {
                    setContext(deepCopy(initialContext));
                }}
                onExportExcel={createExcelExport}
                onSnapshot={createSnapshot}
                onToggleFavorite={toggleFavorite}
                onReload={loadData}
                onSwitchReport={switchReport}
                onChangeContextRules={changeContextRules}
                matchedPages={matchedPages}
                isMatchedPagesLoading={isMatchedPagesLoading}
                hasMatchedPagesError={hasMatchedPagesError}
                onGetMatchedPages={getMatchedPages}
            />
        </Layout>
    );
}
